/**
 * ResumeBlueprintPage — the Counterpart Breakdown generation + view surface.
 *
 * File + component name kept (internal identifier, per LOCKED 2026-05-25 spec —
 * renaming buys zero user value); user-facing copy renamed to "Candidate
 * Breakdown" throughout. Pre-Offer / Post-Offer mode-gate removed. DOB is a
 * required generation input with two supply paths the manager picks between:
 * manager_manual (primary, default) and magic_invite (optional alternative).
 *
 * Sourced from:
 *  · design/mockups/V1_Candidate_Breakdown_Creation_Flow.html (creation form)
 *  · design/mockups/V1_Candidate_Breakdown_Card.html        (rendered surface
 *    + card⇄briefing toggle; default view on open = card)
 */

import { useState } from "react";
import { useSession } from "../lib/useSession.js";
import {
  Wordmark,
  SectionHeader,
  Callout,
  SeriesBanner,
  AuditFooter,
  MarkdownV2,
  Chip,
  PrintToPdfButton,
  RecentBlueprintsPanel,
  CandidateBreakdownCard,
} from "../components/index.js";
import MentionAutocomplete from "../components/MentionAutocomplete.jsx";
import BlueprintChat from "../components/BlueprintChat.jsx";
import StreamingMarkdown from "../components/StreamingMarkdown.jsx";
import { useStreamingBlueprint } from "../lib/use-streaming-blueprint.js";
import { terminalActionButtonStyle, terminalActionButtonDisabledStyle } from "../lib/buttonStyles.js";

const RESUME_PRESETS = [
  { label: "Suggest 3 questions to ask this person",             preset_text: "Suggest 3 questions to ask this person, targeted at the gaps in their background." },
  { label: "Draft a proposal framing for this person",          preset_text: "Draft a proposal framing that lands for this person's behavioral profile." },
  { label: "Worst-case month-2 derailment",                     preset_text: "What would the worst-case derailment look like in the second month working with this person, and what's the leading indicator?" },
  { label: "How should I open the first conversation?",         preset_text: "How should I open the first conversation with this person?" },
  { label: "What should I verify before committing?",           preset_text: "What should I verify before committing, and how should I check it?" },
];

export default function ResumeBlueprintPage() {
  const { session } = useSession();

  // DOB supply path picker — "manager_manual" is the primary, default path.
  // "magic_invite" routes through the existing Lite Profile + consent-send-invite
  // flow; the breakdown isn't generated until the candidate confirms (V1.x
  // follow-up: auto-trigger on consent-redeem).
  const [dobSupplyPath, setDobSupplyPath] = useState("manager_manual");

  const [resumeText, setResumeText] = useState("");
  const [jdText, setJdText] = useState("");
  const [candidateFirstName, setCandidateFirstName] = useState("");
  const [candidateLastName, setCandidateLastName] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [magicInviteEmail, setMagicInviteEmail] = useState("");

  const [candidateMentions, setCandidateMentions] = useState([]);
  const [candidateMentionText, setCandidateMentionText] = useState("");
  const [hiringManagerMentions, setHiringManagerMentions] = useState([]);
  const [hiringManagerMentionText, setHiringManagerMentionText] = useState("");

  const stream = useStreamingBlueprint({
    syncUrl: "/.netlify/functions/resume-blueprint",
    streamUrl: "/api/resume-blueprint/stream",
    bearerToken: session?.access_token,
  });
  const { streaming, setStreaming, generating, streamRequest, blueprint, error, submit, onStreamDone } = stream;
  const [localError, setLocalError] = useState(null);
  const [magicInviteStatus, setMagicInviteStatus] = useState(null);

  const candidateDisplayName = candidateFirstName
    ? (candidateLastName ? `${candidateFirstName} ${candidateLastName}` : candidateFirstName)
    : (candidateMentions[0]?.display_name || "Candidate");

  const generate = async () => {
    if (!resumeText.trim()) {
      setLocalError("Resume text is required.");
      return;
    }

    if (dobSupplyPath === "manager_manual") {
      const y = parseInt(dobYear, 10), m = parseInt(dobMonth, 10), d = parseInt(dobDay, 10);
      if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
        setLocalError("Date of birth is required — type the person's DOB into the year / month / day fields.");
        return;
      }
      setLocalError(null);
      submit({
        dob_supply_path: "manager_manual",
        candidate_dob: { year: y, month: m, day: d },
        candidate_resume_parse: {
          first_name: candidateFirstName || "Candidate",
          full_name: candidateDisplayName,
          tenure_pattern_summary: "(parsed by client-side resume parser; V1.x route will run server-side)",
          free_text: resumeText,
        },
        job_description: jdText ? { title: "(JD pasted inline)", free_text: jdText } : null,
        hiring_manager_profile_id: hiringManagerMentions[0]?.profile_id || null,
      });
      return;
    }

    // magic_invite path — generation requires a consented Profile. If a Profile
    // is already mentioned (consent on file), generate immediately; otherwise
    // send a fresh magic invite and tell the manager to come back when the
    // candidate confirms.
    if (dobSupplyPath === "magic_invite") {
      if (candidateMentions[0]?.profile_id) {
        // The mentioned Profile is presumed already consented (mention-autocomplete
        // surfaces full Profiles); the server will reject with 403 if not.
        setLocalError(null);
        submit({
          dob_supply_path: "magic_invite",
          candidate_profile_id: candidateMentions[0].profile_id,
          candidate_resume_parse: {
            first_name: candidateMentions[0].display_name || candidateFirstName || "Candidate",
            full_name: candidateMentions[0].display_name || candidateDisplayName,
            tenure_pattern_summary: "(parsed by client-side resume parser; V1.x route will run server-side)",
            free_text: resumeText,
          },
          job_description: jdText ? { title: "(JD pasted inline)", free_text: jdText } : null,
          hiring_manager_profile_id: hiringManagerMentions[0]?.profile_id || null,
        });
        return;
      }
      // No Profile yet — send the invite. The breakdown generates later when
      // the candidate confirms + the manager returns to this surface with the
      // Profile mentioned. (V1.x follow-up: auto-trigger on consent-redeem.)
      if (!magicInviteEmail) {
        setLocalError("Their work email is required to send the magic invite.");
        return;
      }
      if (!candidateFirstName) {
        setLocalError("The person's first name is required to create the Profile that the invite confirms against.");
        return;
      }
      setLocalError(null);
      setMagicInviteStatus({ pending: true });
      try {
        // Step 1: create the Lite Profile (carries the candidate's name + email
        // for the invite to land against).
        const createRes = await fetch("/.netlify/functions/profiles", {
          method: "POST",
          headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({
            display_name: candidateFirstName,
            full_name: candidateDisplayName,
            work_email: magicInviteEmail,
            role_title: null,
            department: null,
            created_via: "candidate_breakdown_magic_invite",
          }),
        });
        const created = await createRes.json();
        if (!createRes.ok) {
          setMagicInviteStatus({ error: created.error || "Failed to create the Lite Profile for the invite." });
          return;
        }
        // Step 2: send the magic invite.
        const inviteRes = await fetch("/.netlify/functions/consent-send-invite", {
          method: "POST",
          headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ profile_id: created.internal_uuid, recipient_email: magicInviteEmail }),
        });
        const inviteBody = await inviteRes.json().catch(() => ({}));
        if (!inviteRes.ok) {
          setMagicInviteStatus({
            error: inviteBody.error || "Magic-invite send failed.",
            profileLink: `/profiles/${created.internal_uuid}`,
          });
          return;
        }
        setMagicInviteStatus({
          sent: true,
          recipient: magicInviteEmail,
          profileLink: `/profiles/${created.internal_uuid}`,
          expiresInDays: inviteBody.expires_in_days,
        });
      } catch (e) {
        setMagicInviteStatus({ error: e.message });
      }
    }
  };

  if (!session) return <p style={{ padding: 24 }}>Sign in to generate a Counterpart Breakdown.</p>;

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ marginBottom: 24 }}>
        <Wordmark scale={1.0} />
      </div>

      {streamRequest && !blueprint ? (
        <StreamingMarkdown
          url={stream.streamUrl}
          bearerToken={session.access_token}
          body={streamRequest}
          onDone={onStreamDone}
          onRetry={stream.retry}
        />
      ) : !blueprint ? (
        <div>
          <RecentBlueprintsPanel
            type="resume"
            bearerToken={session.access_token}
          />
          <SectionHeader index={1} title="New Counterpart Breakdown" />

          <p style={pageSubStyle}>
            A one-page read of the person — strengths, friction points, energy, how to work with them — plus the full briefing with fit, the questions to probe, where you align, and the first move.
          </p>

          <div style={{ display: "grid", gap: 16, marginTop: 20 }}>
            <Field label="Person's first name">
              <input
                type="text"
                value={candidateFirstName}
                onChange={(e) => setCandidateFirstName(e.target.value)}
                placeholder="Renata"
                style={inputStyle}
              />
            </Field>

            <Field label="Person's last name (optional)">
              <input
                type="text"
                value={candidateLastName}
                onChange={(e) => setCandidateLastName(e.target.value)}
                placeholder="Cole"
                style={inputStyle}
              />
            </Field>

            <Field label="Bio / background">
              <textarea
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
                placeholder="Paste a bio, resume, LinkedIn, or background…"
                rows={8}
                style={inputStyle}
              />
            </Field>

            <Field label="Context / what's at stake (optional but high-value)">
              <textarea
                value={jdText}
                onChange={(e) => setJdText(e.target.value)}
                placeholder="Paste the role, deal, or situation…"
                rows={6}
                style={inputStyle}
              />
            </Field>

            <Field label="Your side (optional — unlocks the Fit section)">
              <MentionAutocomplete
                value={hiringManagerMentionText}
                onChange={setHiringManagerMentionText}
                mentions={hiringManagerMentions}
                onMentionsChange={setHiringManagerMentions}
                placeholder="@Marcus"
                rows={2}
                bearerToken={session.access_token}
              />
            </Field>

            <Field label="Date of birth">
              <p style={hintStyle}>
                The read is built from the person's date of birth. Type it in and the breakdown generates immediately.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, maxWidth: 360 }}>
                <input
                  type="number" min="1" max="12" placeholder="MM"
                  value={dobMonth} onChange={(e) => setDobMonth(e.target.value)}
                  style={inputStyle}
                />
                <input
                  type="number" min="1" max="31" placeholder="DD"
                  value={dobDay} onChange={(e) => setDobDay(e.target.value)}
                  style={inputStyle}
                />
                <input
                  type="number" min="1900" max="2100" placeholder="YYYY"
                  value={dobYear} onChange={(e) => setDobYear(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </Field>

            {(localError || error) && <ErrorBox error={localError || error} />}

            <button
              onClick={generate}
              disabled={generating}
              style={generating ? terminalActionButtonDisabledStyle : terminalActionButtonStyle}
            >
              {generating ? "Generating Counterpart Breakdown…" : "Generate Counterpart Breakdown"}
            </button>
            <p style={{ ...hintStyle, marginTop: -4 }}>
              Typically completes in ~20–40 seconds. The card opens first; the full briefing is one click away.
            </p>
          </div>
        </div>
      ) : (
        <RenderedCandidateBreakdown blueprint={blueprint} bearerToken={session.access_token} />
      )}
    </div>
  );
}

/**
 * Rendered surface — the breakdown after generation. Default view is the
 * card; the toggle flips to the full briefing one click away (LOCKED 2026-05-25).
 */
function RenderedCandidateBreakdown({ blueprint, bearerToken }) {
  const [view, setView] = useState("card");          // default-card per LOCKED spec
  const candidateName = blueprint.title?.replace(/^Counterpart Breakdown:\s*/i, "")
                                       ?.replace(/^Resume Blueprint:\s*/i, "")
                                       ?.split(" for ")[0] || "the person";

  // Audit-footer consent record string — surfaces the DOB-source provenance so
  // the four-field strip stays informative even after the "Not for use in
  // hiring decisions" line was dropped.
  const consentRecord = blueprint.dob_supply_path
    ? `${blueprint.dob_supply_path} · ${new Date().toISOString().slice(0, 10)}`
    : null;

  return (
    <article>
      <SeriesBanner type="Counterpart Breakdown" dateLabel={new Date().toISOString().slice(0, 10)} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "24px 0 18px" }}>
        <h1 style={titleStyle}>{blueprint.title}</h1>
        <div style={toggleGroupStyle} role="group" aria-label="View mode">
          <button
            type="button"
            style={view === "card" ? { ...toggleOptionStyle, ...toggleOptionActiveStyle } : toggleOptionStyle}
            onClick={() => setView("card")}
            aria-pressed={view === "card"}
          >
            Card
          </button>
          <button
            type="button"
            style={view === "briefing" ? { ...toggleOptionStyle, ...toggleOptionActiveStyle } : toggleOptionStyle}
            onClick={() => setView("briefing")}
            aria-pressed={view === "briefing"}
          >
            Full briefing
          </button>
        </div>
      </div>

      {blueprint.skills_match_summary && view === "briefing" && (
        <div style={{ display: "flex", gap: 16, fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: "12px", color: "var(--ppr-color-text-muted)", marginBottom: 16 }}>
          <span>✓ {blueprint.skills_match_summary.evidenced} evidenced</span>
          <span>✗ {blueprint.skills_match_summary.missing} missing</span>
          <span>~ {blueprint.skills_match_summary.adjacent} adjacent</span>
          <span>· {blueprint.tailored_questions_count} interview questions</span>
        </div>
      )}

      {view === "card" ? (
        <CandidateBreakdownCard
          candidateName={candidateName}
          cardSections={blueprint.card_sections}
          engineVersion={blueprint.engine_version}
          consentRecord={consentRecord}
          blueprintId={blueprint.blueprint_id}
        />
      ) : (
        <>
          <MarkdownV2 source={blueprint.blueprint_markdown} />
          <AuditFooter
            engineVersion={blueprint.engine_version}
            consentRecord={consentRecord}
            blueprintId={blueprint.blueprint_id}
          />
        </>
      )}

      <BlueprintChat
        blueprintId={blueprint.blueprint_id}
        blueprintType="resume"
        presetPrompts={RESUME_PRESETS}
        bearerToken={bearerToken}
      />

      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
        <PrintToPdfButton label="Save as PDF" />
      </div>
    </article>
  );
}

const inputStyle = {
  padding: "12px 14px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-base)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-primary)",
  width: "100%",
  outline: "none",
  resize: "vertical",
};

const titleStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-4xl)",
  color: "var(--ppr-color-accent-navy)",
  letterSpacing: "var(--ppr-typography-letter-spacing-tight)",
  lineHeight: "var(--ppr-typography-line-height-tight)",
  margin: 0,
};

const pageSubStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: 14,
  color: "var(--ppr-color-text-secondary)",
  margin: "0 0 22px",
  maxWidth: 600,
  lineHeight: 1.55,
};

const hintStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: 12,
  fontStyle: "italic",
  color: "var(--ppr-color-text-muted)",
  margin: "8px 0 0",
  lineHeight: 1.5,
};

const codeStyle = {
  fontFamily: "var(--ppr-typography-font-family-mono)",
  fontSize: 11,
};

const toggleGroupStyle = {
  display: "inline-flex",
  border: "1.5px solid var(--ppr-color-accent-navy)",
  borderRadius: 999,
  overflow: "hidden",
  background: "var(--ppr-color-background-surface)",
};

const toggleOptionStyle = {
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: 12.5,
  fontWeight: 700,
  color: "var(--ppr-color-accent-navy)",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: "7px 16px",
  letterSpacing: "0.02em",
};

const toggleOptionActiveStyle = {
  background: "var(--ppr-color-accent-navy)",
  color: "var(--ppr-color-text-inverse)",
};

function Field({ label, children }) {
  return (
    <div>
      <label style={{
        display: "block",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        color: "var(--ppr-color-text-secondary)",
        marginBottom: 6,
      }}>{label}</label>
      {children}
    </div>
  );
}

function ErrorBox({ error }) {
  return (
    <div style={{
      padding: "12px 14px",
      border: "2px solid var(--ppr-color-accent-coral)",
      background: "var(--ppr-color-accent-coral-very-soft)",
      color: "var(--ppr-color-accent-coral-dark)",
    }}>
      {error}
    </div>
  );
}

function DobOption({ selected, onSelect, title, tag, tagPrimary, description, children }) {
  return (
    <div
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
      style={{
        border: selected ? "1.5px solid var(--ppr-color-accent-navy)" : "1.5px solid var(--ppr-color-border-default)",
        background: selected ? "var(--ppr-color-accent-pale-soft)" : "var(--ppr-color-background-surface)",
        borderRadius: 8,
        padding: "14px 16px",
        cursor: "pointer",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <span style={{
          width: 16, height: 16, borderRadius: 999,
          border: selected ? "1px solid var(--ppr-color-border-default)" : "1px solid var(--ppr-color-border-default)",
          background: selected ? "radial-gradient(var(--ppr-color-accent-navy) 0 4px, #FFF 5px)" : "transparent",
          flexShrink: 0,
        }} />
        <span style={{ fontFamily: "var(--ppr-typography-font-family-ui)", fontSize: 13.5, fontWeight: 700, color: "var(--ppr-color-accent-navy)" }}>
          {title}
        </span>
        <span style={{
          marginLeft: "auto",
          fontFamily: "var(--ppr-typography-font-family-mono)",
          fontSize: 9,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: tagPrimary ? "var(--ppr-color-accent-coral-dark)" : "var(--ppr-color-text-muted)",
          padding: "2px 7px",
          border: tagPrimary ? "1px solid #E6C3B6" : "1px solid var(--ppr-color-border-default)",
          background: tagPrimary ? "var(--ppr-color-accent-coral-very-soft)" : "transparent",
          borderRadius: 999,
        }}>{tag}</span>
      </div>
      <p style={{
        fontFamily: "var(--ppr-typography-font-family-body)",
        fontSize: 12.5, lineHeight: 1.5,
        color: "var(--ppr-color-text-secondary)", margin: "6px 0 0",
      }}>{description}</p>
      {selected && (
        <div style={{
          marginTop: 14, paddingTop: 14,
          borderTop: "1px dashed var(--ppr-color-border-default)",
          display: "grid", gap: 8,
        }}>{children}</div>
      )}
    </div>
  );
}
